import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import { Goal, GoalCategory, GoalStatus, GoalFormData, Milestone } from '../../types/Goal';
import { goalService } from '../../services/goalService';
import { metricService, Metric } from '../../services/metricService';
import { relationshipService } from '../../services/relationshipService';
import { Relationship, ObjectType } from '../../types/Relationship';
import GoalList from './GoalList';
import GoalMapGraph from './GoalMapGraph';
import GoalDependencyForm from './GoalDependencyForm';
import PageHeader from '../common/PageHeader';
import './GoalManagement.css';
import './GoalList.css';

interface GoalManagementProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

export interface GoalManagementRef {
  handleAddGoal: () => void;
}

type ViewMode = 'list' | 'map';

const GoalManagement = forwardRef<GoalManagementRef, GoalManagementProps>(({ user, onClose, onToggleSidebar }, ref) => {
  const navigate = useNavigate();
  const [goals, setGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingGoal, setEditingGoal] = useState<Goal | undefined>();
  const [availableMetrics, setAvailableMetrics] = useState<Metric[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [showMenu, setShowMenu] = useState(false);
  const [goalRelationships, setGoalRelationships] = useState<Relationship[]>([]);
  const [showDependencyForm, setShowDependencyForm] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Form state
  const [formData, setFormData] = useState<GoalFormData>({
    title: '',
    description: '',
    category: GoalCategory.PERSONAL,
    targetDate: '',
    notes: '',
    metric_name: '',
    target_metric_value: undefined
  });

  // Load goals and metrics
  useEffect(() => {
    if (user) {
      loadGoals();
      loadMetrics();
    }
  }, [user]);

  // Load goal relationships when in map view
  useEffect(() => {
    if (user && viewMode === 'map') {
      loadGoalRelationships();
    }
  }, [user, viewMode]);

  // Handle clicks outside menu to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showMenu && menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  const loadMetrics = async () => {
    try {
      const result = await metricService.getAllMetrics({ is_active: true });
      if (result.success && result.metrics) {
        setAvailableMetrics(result.metrics);
      }
    } catch (err: any) {
      console.error('Error loading metrics:', err);
    }
  };

  const loadGoalRelationships = async () => {
    if (!user) return;
    
    try {
      const result = await relationshipService.getRelationships({
        from_object_type: ObjectType.GOAL,
        to_object_type: ObjectType.GOAL,
        limit: 100
      });
      
      if (result.success && result.data?.relationships) {
        setGoalRelationships(result.data.relationships);
      } else if (result.success && result.relationships) {
        setGoalRelationships(result.relationships);
      } else {
        setGoalRelationships([]);
      }
    } catch (err: any) {
      console.error('Error loading goal relationships:', err);
      setGoalRelationships([]);
    }
  };

  const handleCreateDependency = async (fromGoalId: string, toGoalId: string, relationshipType: string, notes?: string) => {
    if (!user) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const result = await relationshipService.createObjectRelationship(
        fromGoalId,
        ObjectType.GOAL,
        toGoalId,
        ObjectType.GOAL,
        notes,
        relationshipType
      );
      
      if (result.success) {
        await loadGoalRelationships();
        setShowDependencyForm(false);
      } else {
        throw new Error(result.message || 'Failed to create dependency');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create dependency');
      console.error('Error creating dependency:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const loadGoals = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const result = await goalService.getUserGoals({
        user_id: user.id.toString()
      });
      
      if (result.success && result.goals) {
        const convertedGoals = result.goals.map(goal => goalService.convertFirebaseGoal(goal));
        setGoals(convertedGoals);
      } else {
        setGoals([]);
      }
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load goals');
      console.error('Error loading goals:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddGoal = () => {
    setEditingGoal(undefined);
    setFormData({
      title: '',
      description: '',
      category: GoalCategory.PERSONAL,
      targetDate: '',
      notes: '',
      metric_name: '',
      target_metric_value: undefined
    });
    setShowForm(true);
  };

  // Expose handleAddGoal to parent component via ref
  useImperativeHandle(ref, () => ({
    handleAddGoal
  }));

  const handleEditGoal = (goal: Goal) => {
    setEditingGoal(goal);
    setFormData({
      title: goal.title,
      description: goal.description,
      category: goal.category,
      targetDate: goal.targetDate || '',
      notes: goal.notes || '',
      metric_name: goal.metric_name || '',
      target_metric_value: goal.target_metric_value
    });
    setShowForm(true);
  };

  const handleSubmitGoal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.description.trim() || !user) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      
      if (editingGoal) {
        // Update existing goal
        const result = await goalService.updateGoal({
          id: editingGoal.id,
          title: formData.title,
          description: formData.description,
          category: formData.category,
          target_date: formData.targetDate || null,
          notes: formData.notes || undefined,
          metric_name: formData.metric_name || undefined,
          target_metric_value: formData.target_metric_value || undefined
        });
        
        if (result.success) {
          // Reload goals to get updated data
          await loadGoals();
        } else {
          throw new Error('Failed to update goal');
        }
      } else {
        // Create new goal
        const result = await goalService.createGoal(user.id.toString(), formData);
        
        if (result.success) {
          // Reload goals to get the new goal
          await loadGoals();
        } else {
          throw new Error(result.message || 'Failed to create goal');
        }
      }
      
      setShowForm(false);
      setEditingGoal(undefined);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to save goal');
      console.error('Error saving goal:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewGoalDetails = (goal: Goal) => {
    navigate(`/goals/${goal.id}`);
  };

  const handleGoalClick = (goalId: string) => {
    navigate(`/goals/${goalId}`);
  };

  const handleDeleteGoal = async (goalId: string) => {
    if (window.confirm('Are you sure you want to delete this goal?')) {
      try {
        setLoading(true);
        const result = await goalService.deleteGoal(goalId);
        
        if (result.success) {
          // Remove goal from local state
          setGoals(prev => prev.filter(g => g.id !== goalId));
        } else {
          throw new Error('Failed to delete goal');
        }
      } catch (err: any) {
        setError(err.message || 'Failed to delete goal');
        console.error('Error deleting goal:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleProgressUpdate = async (goalId: string, newProgress: number) => {
    try {
      const result = await goalService.updateGoalProgress(goalId, newProgress);
      
      if (result.success) {
        // Update local state
        setGoals(prev => prev.map(g => 
          g.id === goalId 
            ? { ...g, progress: newProgress, status: result.completed ? GoalStatus.COMPLETED : g.status }
            : g
        ));
      } else {
        throw new Error('Failed to update progress');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update progress');
      console.error('Error updating progress:', err);
    }
  };

  const getStatusColor = (status: GoalStatus) => {
    switch (status) {
      case GoalStatus.NOT_STARTED: return '#6c757d';
      case GoalStatus.IN_PROGRESS: return '#007bff';
      case GoalStatus.COMPLETED: return '#28a745';
      case GoalStatus.ON_HOLD: return '#ffc107';
      case GoalStatus.CANCELLED: return '#dc3545';
      default: return '#6c757d';
    }
  };




  if (!user) {
    return (
      <div className="goal-management">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">Goals</h1>}
        />
        <div className="auth-required">
          <p>Please sign in to manage your goals.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="goal-management">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="page-header-center-title">Goals</h1>}
        rightActions={
          <div className="goal-management-menu-container" ref={menuRef}>
            <button
              className="goal-management-menu-btn"
              onClick={() => setShowMenu(!showMenu)}
              title="Menu"
            >
              ⋮
            </button>
            {showMenu && (
              <div className="goal-management-menu-dropdown">
                <button
                  className="goal-management-menu-item"
                  onClick={() => {
                    handleAddGoal();
                    setShowMenu(false);
                  }}
                >
                  + Add Goal
                </button>
                <div className="goal-management-menu-separator"></div>
                <button
                  className={`goal-management-menu-item ${viewMode === 'list' ? 'active' : ''}`}
                  onClick={() => {
                    setViewMode('list');
                    setShowMenu(false);
                  }}
                >
                  List View
                </button>
                <button
                  className={`goal-management-menu-item ${viewMode === 'map' ? 'active' : ''}`}
                  onClick={() => {
                    setViewMode('map');
                    setShowMenu(false);
                  }}
                >
                  Map View
                </button>
                <div className="goal-management-menu-separator"></div>
                {viewMode === 'map' && (
                  <>
                    <button
                      className="goal-management-menu-item"
                      onClick={() => {
                        setShowDependencyForm(true);
                        setShowMenu(false);
                      }}
                    >
                      Add Dependency
                    </button>
                    <div className="goal-management-menu-separator"></div>
                  </>
                )}
                <button
                  className="goal-management-menu-item"
                  onClick={async () => {
                    try {
                      setLoading(true);
                      setError(null);
                      const result = await goalService.updateAllGoalsProgress();
                      if (result.success) {
                        await loadGoals();
                        setShowMenu(false);
                      } else {
                        throw new Error(result.message || 'Failed to update progress');
                      }
                    } catch (err: any) {
                      setError(err.message || 'Failed to update goal progress');
                      console.error('Error updating goal progress:', err);
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading}
                >
                  {loading ? 'Updating...' : 'Update Progress'}
                </button>
              </div>
            )}
          </div>
        }
      />

      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="goal-content">
        {viewMode === 'list' ? (
          <GoalList
            goals={goals}
            loading={loading}
            onGoalClick={handleViewGoalDetails}
            onAddGoal={handleAddGoal}
            showAddButton={true}
            emptyStateTitle="No goals yet"
            emptyStateMessage="Start by creating your first goal to track your progress!"
            emptyStateButtonText="Create Your First Goal"
          />
        ) : (
          <GoalMapGraph
            goals={goals}
            relationships={goalRelationships}
            onGoalClick={handleGoalClick}
          />
        )}
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowForm(false);
          }
        }}>
          <div className="modal-content goal-form-modal">
            <div className="modal-header">
              <h2>{editingGoal ? 'Edit Goal' : 'Create New Goal'}</h2>
              <button className="close-btn" onClick={() => setShowForm(false)}>×</button>
            </div>
            
            <form onSubmit={handleSubmitGoal} className="goal-form">
              <div className="form-group">
                <label htmlFor="title">Title *</label>
                <input
                  type="text"
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter goal title..."
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description *</label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe your goal in detail..."
                  rows={4}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="category">Category</label>
                <select
                  id="category"
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value as GoalCategory }))}
                >
                  {Object.values(GoalCategory).map(category => (
                    <option key={category} value={category}>
                      {category.replace('_', ' ').toUpperCase()}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="targetDate">Target Date</label>
                <input
                  type="date"
                  id="targetDate"
                  value={formData.targetDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, targetDate: e.target.value }))}
                />
              </div>

              <div className="form-group">
                <label htmlFor="notes">Notes</label>
                <textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Additional notes about this goal..."
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label htmlFor="metricName">Link to Metric (Optional)</label>
                <select
                  id="metricName"
                  value={formData.metric_name || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, metric_name: e.target.value || undefined }))}
                >
                  <option value="">No metric</option>
                  {availableMetrics.map(metric => (
                    <option key={metric.id} value={metric.name}>
                      {metric.name} ({metric.category})
                    </option>
                  ))}
                </select>
                {formData.metric_name && (
                  <small style={{ display: 'block', marginTop: '0.25rem', color: '#666' }}>
                    {availableMetrics.find(m => m.name === formData.metric_name)?.description}
                  </small>
                )}
              </div>

              {formData.metric_name && (
                <div className="form-group">
                  <label htmlFor="targetMetricValue">Target Metric Value</label>
                  <input
                    type="number"
                    id="targetMetricValue"
                    value={formData.target_metric_value || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, target_metric_value: e.target.value ? parseFloat(e.target.value) : undefined }))}
                    placeholder="Enter target value..."
                    step="0.01"
                  />
                  {formData.metric_name && (
                    <small style={{ display: 'block', marginTop: '0.25rem', color: '#666' }}>
                      Unit: {availableMetrics.find(m => m.name === formData.metric_name)?.unit}
                    </small>
                  )}
                </div>
              )}

              <div className="form-actions">
                <button type="button" onClick={() => setShowForm(false)} className="cancel-btn">
                  Cancel
                </button>
                <button type="submit" className="submit-btn" disabled={loading}>
                  {loading ? 'Saving...' : (editingGoal ? 'Update Goal' : 'Create Goal')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDependencyForm && (
        <GoalDependencyForm
          goals={goals}
          onSave={handleCreateDependency}
          onCancel={() => setShowDependencyForm(false)}
          loading={loading}
        />
      )}
    </div>
  );
});

GoalManagement.displayName = 'GoalManagement';

export default GoalManagement;
